import type { Patch } from "@freightsimple/shared/types";
import { FormGroup } from "@freightsimple/ui-core/FormLayout/FormGroup";
import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import type { ErrorMessageType } from "@freightsimple/ui-core/FormLayout/FormItem";

import CreditCardsCardAddress from "@/Shared/CreditCards/CustomComponents/CreditCardsCardAddress";
import CreditCardsCardPanel from "@/Shared/CreditCards/Panels/CreditCardsCardPanel";
import type { Address } from "@freightsimple/generated-dashboard-openapi-client/models/Address";
import React from "react";

interface CardEntryFormProps {
  setBillingName: (_: string) => void;
  setBillingAddress: (_: Patch<Address>) => void;
  billingName: string;
  billingAddress: Patch<Address>;
  forceValidation: boolean;
  addresses: Array<Address>;
  onBrandChange?: ((brandName: string) => void) | undefined;
  errorMessageForBillingName: ErrorMessageType;
  onCreditCardNumberError: (message: ErrorMessageType) => void;
  onCreditCardExpirationError: (message: ErrorMessageType) => void;
  onCreditCardCVCError: (message: ErrorMessageType) => void;
  errorMessageForCreditCardNumber: ErrorMessageType;
  errorMessageForCreditCardExpiration: ErrorMessageType;
  errorMessageForCreditCardCVC: ErrorMessageType;
  errorMessageForAddress: ErrorMessageType;
  errorMessageForCity: ErrorMessageType;
  errorMessageForStateOrProvinceCode: ErrorMessageType;
  locationsErrorMessageForPostalCode: ErrorMessageType;
  errorMessageForCountry: ErrorMessageType;
  onValidate: () => void;
  sideBySideLayout?: boolean | undefined;
}

const CreditCardsCardEntryForm: React.FC<CardEntryFormProps> = (
  props: CardEntryFormProps,
) => {
  const cardSection = (
    <CreditCardsCardPanel
      onBillingNameChanged={props.setBillingName}
      forceValidation={props.forceValidation}
      billingName={props.billingName}
      onBrandChange={props.onBrandChange}
      errorMessageForBillingName={props.errorMessageForBillingName}
      onCreditCardCVCError={props.onCreditCardCVCError}
      onCreditCardExpirationError={props.onCreditCardExpirationError}
      onCreditCardNumberError={props.onCreditCardNumberError}
      errorMessageForCreditCardNumber={props.errorMessageForCreditCardNumber}
      errorMessageForCreditCardExpiration={
        props.errorMessageForCreditCardExpiration
      }
      errorMessageForCreditCardCVC={props.errorMessageForCreditCardCVC}
      onValidate={props.onValidate}
    />
  );

  const billingSection = (
    <FormGroup label="Billing Address" labelPosition="above">
      <CreditCardsCardAddress
        addresses={props.addresses}
        address={props.billingAddress}
        onAddressChanged={(deltaAddress) =>
          props.setBillingAddress({ ...props.billingAddress, ...deltaAddress })
        }
        onResetAddress={() => props.setBillingAddress({})}
        validated={props.forceValidation}
        forceValidation={props.forceValidation}
        errorMessageForAddress={props.errorMessageForAddress}
        errorMessageForCity={props.errorMessageForCity}
        errorMessageForStateOrProvinceCode={
          props.errorMessageForStateOrProvinceCode
        }
        locationsErrorMessageForPostalCode={
          props.locationsErrorMessageForPostalCode
        }
        errorMessageForCountry={props.errorMessageForCountry}
      />
    </FormGroup>
  );

  const layout = props.sideBySideLayout ? (
    <Grid columns={2} gap="lg">
      {cardSection}
      {billingSection}
    </Grid>
  ) : (
    <Stack gap={5}>
      {cardSection}
      {billingSection}
    </Stack>
  );

  // No presentation of its own — the form inherits the screen's, so it reads
  // as underlined in the customer flows and boxed in a dense one. Once the
  // user has attempted to submit (forceValidation), reveal every field's
  // error via ShowFormErrors — otherwise errors wait for blur/submit.
  // ShowFormErrors stays mounted and is gated by `show`: conditionally
  // rendering it would remount the subtree and wipe the Stripe card iframes.
  return <ShowFormErrors show={props.forceValidation}>{layout}</ShowFormErrors>;
};

export default CreditCardsCardEntryForm;
